// lib/brands/logoChip.ts
//
// What colour should the tile behind a brand logo be?
//
// Every surface in the app used to answer this the same wrong way: one fixed dark tile for every
// mark, with the image drawn edge to edge. That single decision produces three failures at once.
// A black mark on a dark tile disappears (Hugo Boss, Kiabi). A transparent mark gets a background
// we invented rather than the one it was designed for. And no mark has a margin, so logos crowd
// their tile and look pasted in.
//
// The rule here derives the tile from the mark itself:
//
//   1. The mark has transparency. Only its own pixels count, so measure their luminance and ignore
//      the empty space. Essentially white ink needs a dark tile; anything darker needs a white one.
//   2. The mark is opaque (a JPEG, or a PNG flattened onto a background). It already ships a ground,
//      so use it and the image dissolves into the tile instead of sitting in a visible box. Take the
//      MODAL colour of the border, never the mean: Horace sits on #0A0029 navy, and averaging that
//      border against the lighter pixels inside its frame returns a mid purple that matches nothing.
//      Averaging also reports #F5F5F5 on marks drawn on pure white, which is exactly what left a
//      faint square around the adidas logo.
//
// The measurement runs in the browser, on the image the browser already downloaded, and is cached
// per URL. The brand-logos container reflects any Origin, so the canvas is not tainted and no
// server round trip, database column or backfill is needed. If a future container drops CORS, the
// canvas read throws, `measureLogoChip` returns null, and callers fall back to a neutral tile.

/** A mark drawn in dark ink sits on white. */
export const LIGHT_CHIP = '#FFFFFF';
/** A mark drawn in white sits on near-black rather than pure black, to match the app's surfaces. */
export const DARK_CHIP = '#1C1C1C';
/** Used until a measurement lands, and whenever one is impossible. */
export const NEUTRAL_CHIP = '#F2F2F2';

type LogoChipSource =
  /** Transparent mark, dark ink: white tile. */
  | 'ink-dark'
  /** Transparent mark, white ink: dark tile. */
  | 'ink-light'
  /** Opaque mark: the tile is the mark's own border colour. */
  | 'opaque'
  /** Nothing measurable (empty image, unreadable pixels). */
  | 'unknown';

export type LogoChip = { color: string; source: LogoChipSource };

/** Above this ink luminance a mark is treated as white and moved onto a dark tile. */
const WHITE_INK_LUMINANCE = 190;
/** Below this share of transparent pixels a mark is treated as carrying its own background. */
const TRANSPARENCY_SHARE = 0.12;
/** Alpha at or below this is empty space, not ink. Anti-aliased edges sit above it. */
const EMPTY_ALPHA = 25;

const luminance = (r: number, g: number, b: number): number => 0.2126 * r + 0.7152 * g + 0.0722 * b;

const toHex = (r: number, g: number, b: number): string =>
  '#' +
  [r, g, b]
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();

/**
 * The rule, as a pure function over RGBA pixels so it can be tested without a browser.
 * `pixels` is canvas `ImageData.data`: four bytes per pixel, row major.
 */
export const deriveLogoChip = (pixels: Uint8ClampedArray | number[], width: number, height: number): LogoChip => {
  const count = width * height;
  if (count <= 0 || pixels.length < count * 4) return { color: NEUTRAL_CHIP, source: 'unknown' };

  let inkLuminanceSum = 0;
  let inkPixels = 0;
  let emptyPixels = 0;

  // `at` rather than indexing: the strict config types an index read on a plain number[] as
  // possibly undefined, and a silent 0 there would be a black pixel we never measured.
  const at = (index: number): number => pixels[index] ?? 0;

  for (let i = 0; i < count; i += 1) {
    const o = i * 4;
    if (at(o + 3) <= EMPTY_ALPHA) {
      emptyPixels += 1;
      continue;
    }
    inkLuminanceSum += luminance(at(o), at(o + 1), at(o + 2));
    inkPixels += 1;
  }

  if (inkPixels === 0) return { color: NEUTRAL_CHIP, source: 'unknown' };

  const inkLuminance = inkLuminanceSum / inkPixels;

  if (emptyPixels / count > TRANSPARENCY_SHARE) {
    return inkLuminance > WHITE_INK_LUMINANCE
      ? { color: DARK_CHIP, source: 'ink-light' }
      : { color: LIGHT_CHIP, source: 'ink-dark' };
  }

  // Opaque: the modal colour of the border is the mark's own background. Counted twice over: first
  // in coarse buckets, so compression noise around one colour groups together instead of splitting
  // the vote, then exactly within the winning bucket, so the answer is a colour the image actually
  // contains. Averaging the bucket instead would return #FEFEFE where the mark uses #FFFFFF, and
  // a tile one step off pure white still draws a visible edge.
  const exactCounts = new Map<number, number>();
  const addBorderPixel = (x: number, y: number) => {
    const o = (y * width + x) * 4;
    if (at(o + 3) <= EMPTY_ALPHA) return;
    const packed = (at(o) << 16) | (at(o + 1) << 8) | at(o + 2);
    exactCounts.set(packed, (exactCounts.get(packed) ?? 0) + 1);
  };

  for (let x = 0; x < width; x += 1) {
    addBorderPixel(x, 0);
    addBorderPixel(x, height - 1);
  }
  for (let y = 0; y < height; y += 1) {
    addBorderPixel(0, y);
    addBorderPixel(width - 1, y);
  }

  // Quantise to 16 levels per channel to find the dominant region of colour space.
  const bucketOf = (packed: number): number =>
    ((((packed >> 16) & 0xff) >> 4) << 8) | ((((packed >> 8) & 0xff) >> 4) << 4) | ((packed & 0xff) >> 4);

  const bucketCounts = new Map<number, number>();
  for (const [packed, n] of exactCounts) {
    const bucket = bucketOf(packed);
    bucketCounts.set(bucket, (bucketCounts.get(bucket) ?? 0) + n);
  }

  let topBucket: number | null = null;
  let topBucketCount = 0;
  for (const [bucket, n] of bucketCounts) {
    if (n > topBucketCount) {
      topBucket = bucket;
      topBucketCount = n;
    }
  }

  if (topBucket === null) {
    return inkLuminance > WHITE_INK_LUMINANCE
      ? { color: DARK_CHIP, source: 'ink-light' }
      : { color: LIGHT_CHIP, source: 'ink-dark' };
  }

  let winner = 0;
  let winnerCount = 0;
  for (const [packed, n] of exactCounts) {
    if (bucketOf(packed) !== topBucket) continue;
    if (n > winnerCount) {
      winner = packed;
      winnerCount = n;
    }
  }

  return {
    color: toHex((winner >> 16) & 0xff, (winner >> 8) & 0xff, winner & 0xff),
    source: 'opaque',
  };
};

// ── Browser side: load, measure, cache ────────────────────────────────────────────────────────

/** Marks are measured at this size. Big enough for a stable modal border, small enough to be free. */
const SAMPLE_SIZE = 64;

const memoryCache = new Map<string, LogoChip>();
const inFlight = new Map<string, Promise<LogoChip | null>>();
/**
 * URLs whose pixels we could not read: a host without CORS, a dead file, an SVG with no intrinsic
 * size. Remembered for the session so a table of fifty rows does not retry fifty times per mount.
 * Not persisted: a container gaining CORS should not need a cache bust.
 */
const unmeasurable = new Set<string>();

const STORAGE_KEY = 'fater:brand-logo-chips:v1';
/** Keep the persisted map small; the newest entries win when it overflows. */
const STORAGE_LIMIT = 600;

let storageLoaded = false;
let storageWritePending = false;

const loadStorage = (): void => {
  if (storageLoaded || typeof window === 'undefined') return;
  storageLoaded = true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, LogoChip>;
    for (const [url, chip] of Object.entries(parsed)) {
      if (chip && typeof chip.color === 'string') memoryCache.set(url, chip);
    }
  } catch {
    // A corrupt cache is not worth a broken logo: start empty.
  }
};

/**
 * Persist on the next tick rather than on every measurement. A shortlist table measures fifty marks
 * in a burst, and serialising the whole map fifty times is work nobody asked for.
 */
const saveStorage = (): void => {
  if (typeof window === 'undefined' || storageWritePending) return;
  storageWritePending = true;
  setTimeout(() => {
    storageWritePending = false;
    try {
      const entries = Array.from(memoryCache.entries()).slice(-STORAGE_LIMIT);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch {
      // Storage full or blocked: the in-memory cache still serves this session.
    }
  }, 250);
};

/** The chip for this logo if it is already known, without triggering a measurement. */
export const getCachedLogoChip = (url: string | null | undefined): LogoChip | null => {
  if (!url) return null;
  loadStorage();
  return memoryCache.get(url) ?? null;
};

/**
 * CAN THIS HOST ANSWER A CORS REQUEST AT ALL? Asking one that cannot is not a fallback, it is a
 * request that can only fail, and it fails LOUDLY: the browser logs a CORS violation and an
 * ERR_FAILED for every such image, twice over, because the tile sampler and the <img> each ask.
 * Six red lines in the console from a handful of logos, drowning real errors - reported 2026-08-13.
 *
 * Measured the same day: 3,999 of 4,000 brand logos live in our own container, which reflects any
 * Origin. The remainder are third-party pages (a Wikipedia file redirect, a brand's own site) that
 * send no CORS header, so they were always going to fall back to a neutral tile. They still do -
 * this only stops us asking a question we already know the answer to.
 */
export const canReadLogoPixels = (url: string): boolean => {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (!trimmed) return false;
  // Same-origin by construction: our own proxy routes and inline data.
  if (trimmed.startsWith('/') || trimmed.startsWith('data:')) return true;
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://fater.ai';
    const parsed = new URL(trimmed, origin);
    if (typeof window !== 'undefined' && parsed.origin === window.location.origin) return true;
    // Our asset storage, which is where every logo we control lives.
    return parsed.host.endsWith('.blob.core.windows.net');
  } catch {
    return false;
  }
};

/**
 * Measure a logo's tile colour, once per URL per browser. Returns null when the image cannot be
 * read (a host that cannot answer CORS, a network failure, an SVG with no intrinsic size), so
 * callers keep their neutral tile instead of rendering something wrong.
 */
export const measureLogoChip = async (url: string): Promise<LogoChip | null> => {
  // Never issue a request that can only fail. The tile stays neutral either way; the difference is
  // whether the console fills with CORS violations while it happens.
  if (!canReadLogoPixels(url)) return null;
  loadStorage();
  const cached = memoryCache.get(url);
  if (cached) return cached;
  if (unmeasurable.has(url)) return null;

  const existing = inFlight.get(url);
  if (existing) return existing;

  const task = (async (): Promise<LogoChip | null> => {
    try {
      const image = await loadImage(url);
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      if (!width || !height) {
        unmeasurable.add(url);
        return null;
      }

      const scale = Math.min(1, SAMPLE_SIZE / Math.max(width, height));
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return null;
      context.drawImage(image, 0, 0, w, h);
      // Throws a SecurityError on a tainted canvas, which is the CORS fallback path.
      const data = context.getImageData(0, 0, w, h).data;

      const chip = deriveLogoChip(data, w, h);
      if (chip.source === 'unknown') {
        unmeasurable.add(url);
        return null;
      }
      memoryCache.set(url, chip);
      saveStorage();
      return chip;
    } catch {
      // Most often a host that does not send CORS, so the canvas read is refused. Remember it.
      unmeasurable.add(url);
      return null;
    } finally {
      inFlight.delete(url);
    }
  })();

  inFlight.set(url, task);
  return task;
};

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    // Required for getImageData: without it the canvas is tainted even when the server allows us.
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('logo failed to load'));
    image.src = url;
  });
